/**
 * IndexedDB 实现的 CacheStore。基于 idb 库（~1KB Promise 包装）。
 *
 * 设计要点：
 *   - structured clone：写入读取对象保持 shape，无需 JSON.parse/stringify
 *   - setMany 用单个 readwrite 事务批量写，比 N 次单独 set 快 5-10 倍
 *   - 异常对外暴露由工厂层（index.ts）catch 后降级到内存实现
 */

import { openDB, IDBPDatabase } from 'idb';

import type { CacheStore, CacheStoreEntry, CacheStoreName } from './types';

const DB_NAME = 'nova-cache';
const DB_VERSION = 1;

let _dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (_dbPromise === null) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('lookups')) {
          db.createObjectStore('lookups');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta');
        }
      },
    });
  }
  return _dbPromise;
}

export const indexedDbStore: CacheStore = {
  async get<T = unknown>(store: CacheStoreName, key: string): Promise<T | undefined> {
    const db = await getDB();
    return (await db.get(store, key)) as T | undefined;
  },

  async set<T = unknown>(store: CacheStoreName, key: string, value: T): Promise<void> {
    const db = await getDB();
    await db.put(store, value, key);
  },

  async setMany(entries) {
    if (entries.length === 0) return;
    const db = await getDB();
    const storesUsed = Array.from(new Set(entries.map((e) => e.store))) as CacheStoreName[];
    const tx = db.transaction(storesUsed, 'readwrite');
    await Promise.all(
      entries.map((e) => tx.objectStore(e.store).put(e.value, e.key)),
    );
    await tx.done;
  },

  async delete(store: CacheStoreName, key: string): Promise<void> {
    const db = await getDB();
    await db.delete(store, key);
  },

  async clear(store: CacheStoreName): Promise<void> {
    const db = await getDB();
    await db.clear(store);
  },

  async getAll<T = unknown>(store: CacheStoreName): Promise<Array<CacheStoreEntry<T>>> {
    const db = await getDB();
    const tx = db.transaction(store, 'readonly');
    const keys = (await tx.store.getAllKeys()) as IDBValidKey[];
    const values = (await tx.store.getAll()) as T[];
    await tx.done;
    return keys.map((key, i) => ({ key: String(key), value: values[i] }));
  },

  async deleteMatching(store: CacheStoreName, predicate: (key: string) => boolean): Promise<number> {
    const db = await getDB();
    const tx = db.transaction(store, 'readwrite');
    const allKeys = (await tx.store.getAllKeys()) as IDBValidKey[];
    let count = 0;
    for (const k of allKeys) {
      if (predicate(String(k))) {
        await tx.store.delete(k);
        count += 1;
      }
    }
    await tx.done;
    return count;
  },
};

/**
 * Test-only：重置内部 DB Promise 缓存，让下次 getDB 重新 openDB。
 * 不要在生产代码使用。
 */
export function _resetForTests(): void {
  _dbPromise = null;
}
